import { useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, ChevronLeft, ChevronRight, X } from 'lucide-react';

interface PhotoSummary {
  photo_month: string; // YYYY-MM
  pose_type: string;
}

interface Props {
  /** Currently selected month in YYYY-MM */
  selectedMonth: string;
  /** All photo records the user has — used to compute month indicators */
  photos: PhotoSummary[];
  /** Called with a YYYY-MM when the user picks a month */
  onSelect: (month: string) => void;
}

const MONTH_SHORT = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

function getCurrentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export function ProgressPhotosCalendarPicker({ selectedMonth, photos, onSelect }: Props) {
  const [open, setOpen] = useState(false);
  const [viewYear, setViewYear] = useState(() => parseInt(selectedMonth.split('-')[0], 10));
  const popoverRef = useRef<HTMLDivElement>(null);

  const currentMonth = getCurrentMonth();
  const currentYear = parseInt(currentMonth.split('-')[0], 10);

  // Re-anchor the year when parent changes selection while closed
  useEffect(() => {
    if (!open) {
      setViewYear(parseInt(selectedMonth.split('-')[0], 10));
    }
  }, [selectedMonth, open]);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  // Compute pose count per month in the visible year
  const poseCountByMonth = useMemo(() => {
    const counts: Record<string, Set<string>> = {};
    for (const p of photos) {
      if (!p.photo_month?.startsWith(`${viewYear}-`)) continue;
      (counts[p.photo_month] ||= new Set()).add(p.pose_type);
    }
    const result: Record<string, number> = {};
    for (const [k, v] of Object.entries(counts)) result[k] = v.size;
    return result;
  }, [photos, viewYear]);

  return (
    <div className="relative" ref={popoverRef}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="p-2 rounded-lg border border-border bg-card hover:bg-accent/10 transition-colors"
        aria-label="Saltar a otro mes"
        title="Saltar a otro mes"
      >
        <Calendar className="w-5 h-5" />
      </button>

      {open && (
        <div className="fixed sm:absolute inset-x-3 sm:inset-x-auto bottom-3 sm:bottom-auto sm:top-12 sm:left-1/2 sm:-translate-x-1/2 z-50 sm:w-[320px] bg-card border-2 border-border rounded-xl shadow-2xl p-3">
          {/* Year nav */}
          <div className="flex items-center justify-between mb-3">
            <button
              onClick={() => setViewYear(y => y - 1)}
              className="w-8 h-8 rounded-lg hover:bg-secondary flex items-center justify-center"
              aria-label="Año anterior"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="text-sm font-black uppercase tracking-wider">{viewYear}</span>
            <button
              onClick={() => setViewYear(y => y + 1)}
              disabled={viewYear >= currentYear}
              className="w-8 h-8 rounded-lg hover:bg-secondary flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed"
              aria-label="Año siguiente"
              title={viewYear >= currentYear ? 'No puedes ir a años futuros' : 'Año siguiente'}
            >
              <ChevronRight size={16} />
            </button>
          </div>

          {/* Months grid 3x4 */}
          <div className="grid grid-cols-3 gap-2">
            {MONTH_SHORT.map((label, idx) => {
              const monthStr = `${viewYear}-${String(idx + 1).padStart(2, '0')}`;
              const count = poseCountByMonth[monthStr] || 0;
              const isFuture = monthStr > currentMonth;
              const isSelected = monthStr === selectedMonth;
              const isCurrent = monthStr === currentMonth;
              return (
                <button
                  key={monthStr}
                  onClick={() => {
                    if (isFuture) return;
                    onSelect(monthStr);
                    setOpen(false);
                  }}
                  disabled={isFuture}
                  className={`relative h-16 rounded-lg flex flex-col items-center justify-center transition-colors ${
                    isFuture
                      ? 'opacity-30 cursor-not-allowed'
                      : 'hover:bg-secondary cursor-pointer'
                  } ${
                    isSelected
                      ? 'bg-accent/15 ring-2 ring-accent text-accent font-bold'
                      : isCurrent
                        ? 'bg-secondary/60 font-bold'
                        : 'bg-card'
                  }`}
                  aria-label={`${label} ${viewYear}${count > 0 ? `, ${count} foto${count !== 1 ? 's' : ''}` : ''}`}
                >
                  <span className="text-sm">{label}</span>
                  {/* Pose indicator: 4 mini dots, filled = pose uploaded */}
                  <div className="flex gap-0.5 mt-1">
                    {[0, 1, 2, 3].map(i => (
                      <span
                        key={i}
                        className={`w-1.5 h-1.5 rounded-full ${
                          i < count ? 'bg-accent' : 'bg-muted/50'
                        }`}
                      />
                    ))}
                  </div>
                </button>
              );
            })}
          </div>

          {/* Footer */}
          <div className="mt-3 pt-3 border-t border-border space-y-2">
            <div className="flex items-center justify-between gap-2">
              <button
                onClick={() => {
                  onSelect(currentMonth);
                  setOpen(false);
                }}
                className="text-xs font-bold uppercase tracking-wider px-3 py-2 rounded-lg bg-accent/10 hover:bg-accent/20 text-accent min-h-[36px]"
              >
                Ir al mes actual
              </button>
              <button
                onClick={() => setOpen(false)}
                className="w-9 h-9 rounded-lg hover:bg-secondary flex items-center justify-center"
                aria-label="Cerrar"
              >
                <X size={16} />
              </button>
            </div>
            <div className="flex items-center gap-x-2 text-[10px] text-muted-foreground flex-wrap">
              <div className="flex gap-0.5 items-center">
                <span className="w-1.5 h-1.5 rounded-full bg-accent" />
                <span className="w-1.5 h-1.5 rounded-full bg-accent" />
                <span className="w-1.5 h-1.5 rounded-full bg-accent" />
                <span className="w-1.5 h-1.5 rounded-full bg-accent" />
              </div>
              <span>= 4 poses subidas (frente, lados, espalda)</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
